import React, { useCallback } from 'react';
import { ALL } from '~/features/base/constants/filterOptions';
import without from 'lodash/fp/without';
import { FormattedMessage } from 'react-intl';
import Tag from '@rio-cloud/rio-uikit/lib/es/Tag';
import TagList from '@rio-cloud/rio-uikit/lib/es/TagList';
import { filterEmptyInstalledDeliverables, getInstalledDeliverables } from '~/features/devices/utils/deviceQuery';
import { InstalledDeliverableRange } from '~/features/deliverables/utils/InstalledDeliverableRange';
import { isStringOrStringArray } from '~/features/base/utils/stringUtils';

interface DeviceQuery {
    hwVariant?: string;
    hwVariantList?: string[];
    distroVersion?: string[];
    deviceType?: string[];
    swVersion?: string[];
    swVersionRange?: { minVersion?: string; maxVersion?: string };
    swVersionMin?: string;
    swVersionMax?: string;
    vehicleManufacturerSparePartNumber?: string[];
    inCustomerFleet?: boolean;
    updatesActive?: boolean;
    updateBlockedReason?: string;
    testReleasesActive?: boolean;
    deviceStatusType?: string[];
    deviceStatusNames?: string[];
    productionMode?: boolean;
    hasMmtProperties?: boolean;
    vehicleEnergyTypes?: string[];
    mmtHeadUnits?: string[];
    mmtFunctions?: string[];
    vehicleVariantsWhitelist?: string[];
    vehicleGroupWhitelist?: string[];
    vehicleGroupBlacklist?: string[];
    lastEssentialImageReportedVersion?: string[];
    minMileage?: string;
    maxMileage?: string;
    installedDeliverables?: string[];
    replaced?: boolean;
}

interface DeviceFilterDisplayProps {
    defaultedQuery?: DeviceQuery;
    updateFilter?: (filters: Partial<DeviceQuery>) => void;
}

const DeviceFilterDisplay: React.FC<DeviceFilterDisplayProps> = ({
    defaultedQuery = {},
    updateFilter = () => {}
}) => {
    const removeHwVariant = useCallback(() => {
        updateFilter({ hwVariant: undefined });
    }, [updateFilter]);

    const removeHwVariantFromWhitelist = useCallback((hardwareVariant: string) => {
        const updatedHardwareVariants = without([hardwareVariant], defaultedQuery.hwVariantList);
        updateFilter({ hwVariantList: updatedHardwareVariants });
    }, [updateFilter, defaultedQuery.hwVariantList]);

    const removeDistroVersion = useCallback((version: string) => {
        const updatedVersions = without([version], defaultedQuery.distroVersion);
        updateFilter({ distroVersion: updatedVersions });
    }, [updateFilter, defaultedQuery.distroVersion]);

    const removeDeviceType = useCallback((deviceType: string) => {
        const updatedDeviceTypes = without([deviceType], defaultedQuery.deviceType);
        updateFilter({ deviceType: updatedDeviceTypes });
    }, [updateFilter, defaultedQuery.deviceType]);

    const removeBaseSoftwareVersion = useCallback((version?: string) => {
        if (version) {
            const updatedVersions = without([version], defaultedQuery.swVersion as string[]);
            updateFilter({ swVersion: updatedVersions });
        } else {
            updateFilter({ swVersion: undefined, swVersionRange: undefined });
        }
    }, [updateFilter, defaultedQuery.swVersion]);

    const removeVehicleVariantFromWhitelist = useCallback((vehicleVariant: string) => {
        const updatedVehicleVariants = without([vehicleVariant], defaultedQuery.vehicleVariantsWhitelist);
        updateFilter({ vehicleVariantsWhitelist: updatedVehicleVariants });
    }, [updateFilter, defaultedQuery.vehicleVariantsWhitelist]);

    const removeGroupFromWhitelist = useCallback((group: string) => {
        const updatedGroups = without([group], defaultedQuery.vehicleGroupWhitelist);
        updateFilter({ vehicleGroupWhitelist: updatedGroups });
    }, [updateFilter, defaultedQuery.vehicleGroupWhitelist]);

    const removeGroupFromBlacklist = useCallback((group: string) => {
        const updatedGroups = without([group], defaultedQuery.vehicleGroupBlacklist);
        updateFilter({ vehicleGroupBlacklist: updatedGroups });
    }, [updateFilter, defaultedQuery.vehicleGroupBlacklist]);

    const removeVehicleManufacturerSparePartNumber = useCallback((sparePartNumber: string) => {
        const updatedSparePartNumbers = without([sparePartNumber], defaultedQuery.vehicleManufacturerSparePartNumber);
        updateFilter({ vehicleManufacturerSparePartNumber: updatedSparePartNumbers });
    }, [updateFilter, defaultedQuery.vehicleManufacturerSparePartNumber]);

    const removeInCustomerFleet = useCallback(() => {
        updateFilter({ inCustomerFleet: undefined });
    }, [updateFilter]);

    const removeUpdatesActive = useCallback(() => {
        updateFilter({ updatesActive: undefined, updateBlockedReason: undefined });
    }, [updateFilter]);

    const removeTestReleasesActive = useCallback(() => {
        updateFilter({ testReleasesActive: undefined });
    }, [updateFilter]);

    const removeDeviceStatusType = useCallback((statusType: string) => {
        const updatedStatusTypes = without([statusType], defaultedQuery.deviceStatusType);
        updateFilter({ deviceStatusType: updatedStatusTypes });
    }, [updateFilter, defaultedQuery.deviceStatusType]);

    const removeDeviceStatusNames = useCallback((statusName: string) => {
        const updatedStatusNames = without([statusName], defaultedQuery.deviceStatusNames);
        updateFilter({ deviceStatusNames: updatedStatusNames });
    }, [updateFilter, defaultedQuery.deviceStatusNames]);

    const removeProductionMode = useCallback(() => {
        updateFilter({ productionMode: undefined });
    }, [updateFilter]);

    const removeLastReportedEssentialImage = useCallback((version: string) => {
        const updatedVersions = without([version], defaultedQuery.lastEssentialImageReportedVersion);
        updateFilter({ lastEssentialImageReportedVersion: updatedVersions });
    }, [updateFilter, defaultedQuery.lastEssentialImageReportedVersion]);

    const removeMinMileage = useCallback(() => {
        updateFilter({ minMileage: undefined });
    }, [updateFilter]);

    const removeMaxMileage = useCallback(() => {
        updateFilter({ maxMileage: undefined });
    }, [updateFilter]);

    const removeInstalledDeliverable = useCallback((deliverable: any, deliverables: any[]) => {
        const deliverablesUpdated = deliverables.filter(d => d.deliverableId !== deliverable.deliverableId);
        updateFilter({ installedDeliverables: deliverablesUpdated.map(d => InstalledDeliverableRange.stringify(d)) });
    }, [updateFilter]);

    const removeReplaced = useCallback(() => {
        updateFilter({ replaced: undefined });
    }, [updateFilter]);

    const removeHasMmtProperties = useCallback(() => {
        updateFilter({ hasMmtProperties: undefined });
    }, [updateFilter]);

    const removeVehicleEnergyType = useCallback((energyType: string) => {
        const updatedEnergyTypes = without([energyType], defaultedQuery.vehicleEnergyTypes);
        updateFilter({ vehicleEnergyTypes: updatedEnergyTypes });
    }, [updateFilter, defaultedQuery.vehicleEnergyTypes]);

    const removeMmtHeadUnit = useCallback((headUnit: string) => {
        const updatedHeadUnits = without([headUnit], defaultedQuery.mmtHeadUnits);
        updateFilter({ mmtHeadUnits: updatedHeadUnits });
    }, [updateFilter, defaultedQuery.mmtHeadUnits]);

    const removeMmtFunction = useCallback((mmtFunction: string) => {
        const updatedFunctions = without([mmtFunction], defaultedQuery.mmtFunctions);
        updateFilter({ mmtFunctions: updatedFunctions });
    }, [updateFilter, defaultedQuery.mmtFunctions]);

    const tags: JSX.Element[] = [];

    if (defaultedQuery.hwVariant && defaultedQuery.hwVariant !== ALL) {
        tags.push(
            <Tag key="hwVariant" size='small' deletable onClick={removeHwVariant}>
                {`HW prefix: ${defaultedQuery.hwVariant}`}
            </Tag>
        );
    }

    if (defaultedQuery.hwVariantList) {
        if (Array.isArray(defaultedQuery.hwVariantList)) {
            defaultedQuery.hwVariantList.forEach((item, index) => {
                tags.push(
                    <Tag key={`hwVariantList-${index}`} size='small' deletable onClick={() => removeHwVariantFromWhitelist(item)}>
                        {`HV: ${item}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="hwVariantListSingle" size='small' deletable onClick={() => removeHwVariantFromWhitelist(defaultedQuery.hwVariantList as any)}>
                    {`HV: ${defaultedQuery.hwVariantList}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.distroVersion) {
        if (Array.isArray(defaultedQuery.distroVersion)) {
            defaultedQuery.distroVersion.forEach((version, index) => {
                if (version !== ALL) {
                    tags.push(
                        <Tag key={`distroVersion-${index}`} size='small' deletable onClick={() => removeDistroVersion(version)}>
                            {`DV: ${version}`}
                        </Tag>
                    );
                }
            });
        } else if (defaultedQuery.distroVersion !== ALL) {
            tags.push(
                <Tag key="distroVersionSingle" size='small' deletable onClick={() => removeDistroVersion(defaultedQuery.distroVersion as any)}>
                    {`DV: ${defaultedQuery.distroVersion}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.deviceType) {
        if (Array.isArray(defaultedQuery.deviceType)) {
            defaultedQuery.deviceType.forEach((item, index) => {
                tags.push(
                    <Tag key={`deviceType-${index}`} size='small' deletable onClick={() => removeDeviceType(item)}>
                        {item}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="deviceTypeSingle" size='small' deletable onClick={() => removeDeviceType(defaultedQuery.deviceType as any)}>
                    {defaultedQuery.deviceType}
                </Tag>
            );
        }
    }

    const swVersionRange = defaultedQuery.swVersionRange ||
        (defaultedQuery.swVersionMin || defaultedQuery.swVersionMax
            ? { minVersion: defaultedQuery.swVersionMin, maxVersion: defaultedQuery.swVersionMax }
            : null);

    if (swVersionRange && (swVersionRange.minVersion || swVersionRange.maxVersion)) {
        const { minVersion, maxVersion } = swVersionRange;
        const rangeText = minVersion && maxVersion ? `${minVersion} - ${maxVersion}` : minVersion ? `≥ ${minVersion}` : maxVersion ? `≤ ${maxVersion}` : '';
        if (rangeText) {
            tags.push(
                <Tag key="swVersionRange" size='small' deletable onClick={() => removeBaseSoftwareVersion()}>
                    {`SV: ${rangeText}`}
                </Tag>
            );
        }
    } else if (defaultedQuery.swVersion && Array.isArray(defaultedQuery.swVersion) && defaultedQuery.swVersion.length > 0) {
        defaultedQuery.swVersion.forEach((version, index) => {
            if (version !== ALL) {
                tags.push(
                    <Tag key={`swVersion-${index}`} size='small' deletable onClick={() => removeBaseSoftwareVersion(version)}>
                        {`SV: ${version}`}
                    </Tag>
                );
            }
        });
    }

    if (defaultedQuery.vehicleManufacturerSparePartNumber) {
        if (Array.isArray(defaultedQuery.vehicleManufacturerSparePartNumber)) {
            defaultedQuery.vehicleManufacturerSparePartNumber.forEach((item, index) => {
                tags.push(
                    <Tag key={`vmspn-${index}`} size='small' deletable onClick={() => removeVehicleManufacturerSparePartNumber(item)}>
                        {`VMSPN: ${item}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="vmspnSingle" size='small' deletable onClick={() => removeVehicleManufacturerSparePartNumber(defaultedQuery.vehicleManufacturerSparePartNumber as any)}>
                    {`VMSPN: ${defaultedQuery.vehicleManufacturerSparePartNumber}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.inCustomerFleet === true) {
        tags.push(
            <Tag key="inCustomerFleetTrue" size='small' deletable onClick={removeInCustomerFleet}>
                <FormattedMessage id='intl-msg:inCustomerFleet'/>
            </Tag>
        );
    }

    if (defaultedQuery.inCustomerFleet === false) {
        tags.push(
            <Tag key="inCustomerFleetFalse" size='small' deletable onClick={removeInCustomerFleet}>
                <FormattedMessage id='intl-msg:notInCustomerFleet'/>
            </Tag>
        );
    }

    if (defaultedQuery.updatesActive === true) {
        tags.push(
            <Tag key="updatesActiveTrue" size='small' deletable onClick={removeUpdatesActive}>
                <FormattedMessage id='intl-msg:notBlocked'/>
            </Tag>
        );
    }

    if (defaultedQuery.updatesActive === false) {
        tags.push(
            <Tag key="updatesActiveFalse" size='small' deletable onClick={removeUpdatesActive}>
                <FormattedMessage id='intl-msg:blocked'/>
            </Tag>
        );
    }

    if (defaultedQuery.testReleasesActive === true) {
        tags.push(
            <Tag key="testReleasesActiveTrue" size='small' deletable onClick={removeTestReleasesActive}>
                <FormattedMessage id='intl-msg:testReleasesActive'/>
            </Tag>
        );
    }

    if (defaultedQuery.testReleasesActive === false) {
        tags.push(
            <Tag key="testReleasesActiveFalse" size='small' deletable onClick={removeTestReleasesActive}>
                <FormattedMessage id='intl-msg:testReleasesNotActive'/>
            </Tag>
        );
    }

    if (defaultedQuery.deviceStatusType) {
        if (Array.isArray(defaultedQuery.deviceStatusType)) {
            defaultedQuery.deviceStatusType.forEach((item, index) => {
                tags.push(
                    <Tag key={`deviceStatusType-${index}`} size='small' deletable onClick={() => removeDeviceStatusType(item)}>
                        <FormattedMessage id='intl-msg:deviceHealthTypes'/>
                        {`: ${item.toUpperCase()}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="deviceStatusTypeSingle" size='small' deletable onClick={() => removeDeviceStatusType(defaultedQuery.deviceStatusType as any)}>
                    <FormattedMessage id='intl-msg:deviceHealthTypes'/>
                    {`: ${(defaultedQuery.deviceStatusType as any).toUpperCase()}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.deviceStatusNames) {
        if (Array.isArray(defaultedQuery.deviceStatusNames)) {
            defaultedQuery.deviceStatusNames.forEach((item, index) => {
                tags.push(
                    <Tag key={`deviceStatusNames-${index}`} size='small' deletable onClick={() => removeDeviceStatusNames(item)}>
                        <FormattedMessage id='intl-msg:deviceStatus'/>
                        {`: ${item.toUpperCase()}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="deviceStatusNamesSingle" size='small' deletable onClick={() => removeDeviceStatusNames(defaultedQuery.deviceStatusNames as any)}>
                    <FormattedMessage id='intl-msg:deviceStatus'/>
                    {`: ${(defaultedQuery.deviceStatusNames as any).toUpperCase()}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.productionMode === true || defaultedQuery.productionMode === false) {
        tags.push(
            <Tag key="productionMode" size='small' deletable onClick={removeProductionMode}>
                <FormattedMessage id='intl-msg:deviceProdMode'/>: {defaultedQuery.productionMode ?
                    <FormattedMessage id='intl-msg:productionModeActive'/> :
                    <FormattedMessage id='intl-msg:productionModeNotActive'/>
                }
            </Tag>
        );
    }

    if (defaultedQuery.vehicleVariantsWhitelist) {
        if (Array.isArray(defaultedQuery.vehicleVariantsWhitelist)) {
            defaultedQuery.vehicleVariantsWhitelist.forEach((item, index) => {
                tags.push(
                    <Tag key={`vehicleVariant-${index}`} size='small' deletable onClick={() => removeVehicleVariantFromWhitelist(item)}>
                        {`VV: ${item}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="vehicleVariantSingle" size='small' deletable onClick={() => removeVehicleVariantFromWhitelist(defaultedQuery.vehicleVariantsWhitelist as any)}>
                    {`VV: ${defaultedQuery.vehicleVariantsWhitelist}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.vehicleGroupWhitelist) {
        if (Array.isArray(defaultedQuery.vehicleGroupWhitelist)) {
            defaultedQuery.vehicleGroupWhitelist.forEach((item, index) => {
                tags.push(
                    <Tag key={`groupWhitelist-${index}`} size='small' deletable onClick={() => removeGroupFromWhitelist(item)}>
                        {`WLG: ${item}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="groupWhitelistSingle" size='small' deletable onClick={() => removeGroupFromWhitelist(defaultedQuery.vehicleGroupWhitelist as any)}>
                    {`WLG: ${defaultedQuery.vehicleGroupWhitelist}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.vehicleGroupBlacklist) {
        if (Array.isArray(defaultedQuery.vehicleGroupBlacklist)) {
            defaultedQuery.vehicleGroupBlacklist.forEach((item, index) => {
                tags.push(
                    <Tag key={`groupBlacklist-${index}`} size='small' deletable onClick={() => removeGroupFromBlacklist(item)}>
                        {`BLG: ${item}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="groupBlacklistSingle" size='small' deletable onClick={() => removeGroupFromBlacklist(defaultedQuery.vehicleGroupBlacklist as any)}>
                    {`BLG: ${defaultedQuery.vehicleGroupBlacklist}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.lastEssentialImageReportedVersion) {
        if (Array.isArray(defaultedQuery.lastEssentialImageReportedVersion)) {
            defaultedQuery.lastEssentialImageReportedVersion.forEach((version, index) => {
                tags.push(
                    <Tag key={`essentialImage-${index}`} size='small' deletable onClick={() => removeLastReportedEssentialImage(version)}>
                        {version ? `SV:'essential-image'@${version}` : `SV:${version}`}
                    </Tag>
                );
            });
        } else {
            tags.push(
                <Tag key="essentialImageSingle" size='small' deletable onClick={() => removeLastReportedEssentialImage(defaultedQuery.lastEssentialImageReportedVersion as any)}>
                    {defaultedQuery.lastEssentialImageReportedVersion ? `SV:'essential-image'@${defaultedQuery.lastEssentialImageReportedVersion}` : `SV:${defaultedQuery.lastEssentialImageReportedVersion}`}
                </Tag>
            );
        }
    }

    if (defaultedQuery.minMileage) {
        tags.push(
            <Tag key="minMileage" size='small' deletable onClick={removeMinMileage}>
                {`minKm: ${defaultedQuery.minMileage}`}
            </Tag>
        );
    }

    if (defaultedQuery.maxMileage) {
        tags.push(
            <Tag key="maxMileage" size='small' deletable onClick={removeMaxMileage}>
                {`maxKm: ${defaultedQuery.maxMileage}`}
            </Tag>
        );
    }

    if (defaultedQuery.installedDeliverables) {
        const deliverables = isStringOrStringArray(defaultedQuery.installedDeliverables)
            ? getInstalledDeliverables(defaultedQuery.installedDeliverables)
            : filterEmptyInstalledDeliverables(defaultedQuery.installedDeliverables);
        deliverables?.forEach((deliverable, index) => {
            tags.push(
                <Tag key={`deliverable-${index}`} size='small' deletable onClick={() => removeInstalledDeliverable(deliverable, deliverables)}>
                    {`Inst. Deliv: ${InstalledDeliverableRange.generateTag(deliverable)}`}
                </Tag>
            );
        });
    }

    if (defaultedQuery.replaced !== undefined) {
        tags.push(
            <Tag key="replaced" size='small' deletable onClick={removeReplaced}>
                {defaultedQuery.replaced ?
                    <FormattedMessage id='intl-msg:replaced'/> :
                    <FormattedMessage id='intl-msg:notReplaced'/>
                } devices
            </Tag>
        );
    }

    if (defaultedQuery.hasMmtProperties === true || defaultedQuery.hasMmtProperties === false) {
        tags.push(
            <Tag key="hasMmtProperties" size='small' deletable onClick={removeHasMmtProperties}>
                <FormattedMessage id='intl-msg:hasMmtProperties'/>: {defaultedQuery.hasMmtProperties ?
                    'Has MMT Properties' :
                    'No MMT Properties'
                }
            </Tag>
        );
    }

    if (defaultedQuery.vehicleEnergyTypes && defaultedQuery.vehicleEnergyTypes.length > 0) {
        defaultedQuery.vehicleEnergyTypes.forEach((item, index) => {
            tags.push(
                <Tag key={`vehicleEnergyTypes-${index}`} size='small' deletable onClick={() => removeVehicleEnergyType(item)}>
                    <FormattedMessage id='intl-msg:vehicleEnergyType'/>: {item}
                </Tag>
            );
        });
    }

    if (defaultedQuery.mmtHeadUnits && defaultedQuery.mmtHeadUnits.length > 0) {
        defaultedQuery.mmtHeadUnits.forEach((item, index) => {
            tags.push(
                <Tag key={`mmtHeadUnits-${index}`} size='small' deletable onClick={() => removeMmtHeadUnit(item)}>
                    <FormattedMessage id='intl-msg:mmtHeadUnit'/>: {item}
                </Tag>
            );
        });
    }

    if (defaultedQuery.mmtFunctions && defaultedQuery.mmtFunctions.length > 0) {
        defaultedQuery.mmtFunctions.forEach((item, index) => {
            tags.push(
                <Tag key={`mmtFunctions-${index}`} size='small' deletable onClick={() => removeMmtFunction(item)}>
                    <FormattedMessage id='intl-msg:mmtFunctions'/>: {item}
                </Tag>
            );
        });
    }

    return tags.length > 0 ? (
        <div className='display-flex row padding-left-10'>
            <div>
                <FormattedMessage id='intl-msg:filteredBy'/>
            </div>
            <div>
                <TagList className='padding-left-5'>
                    {tags}
                </TagList>
            </div>
        </div>
    ) : null;
};

export default DeviceFilterDisplay;
